import { apiSlice } from './apiSlice';
import { notificationListTag, notificationsApi } from './notificationsApi';
import { 
  Application, 
  ApplicationFilters,
  CreateApplicationRequest
} from '../../types';

export interface ApplicationsResponse {
  applications: Application[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UpdateApplicationRequest extends Partial<CreateApplicationRequest> {
  id: number;
}

export interface UpdateApplicationStatusRequest {
  id: number;
  status: string;
  notes?: string;
}

export interface ApplicationEvent {
  id: string;
  eventType: string;
  title: string;
  description: string;
  eventDate: string;
  routeId?: number;
  routeNumber?: string;
  source: 'route' | 'cargo';
  createdBy?: string;
  cargoId?: number;
  cargoName?: string;
}

export const applicationsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Клиенты для фильтра
    getClientsForFilter: builder.query<{ id: number; name: string }[], void>({
      query: () => '/references/clients',
      transformResponse: (response: any) => response?.data || response || [],
      providesTags: ['Application']
    }),
    // Получение списка заявок
    getApplications: builder.query<ApplicationsResponse, (ApplicationFilters & {
      page?: number;
      limit?: number;
      search?: string;
      source?: 'internal' | 'external';
      statusGroup?: 'in_work' | 'completed' | 'cancelled';
    })>({
      query: ({ page = 1, limit = 10, ...filters }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        });
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });

        return `/applications?${params.toString()}`;
      },
      transformResponse: (response: any): ApplicationsResponse => {
        console.log('🔄 API Response transformation:', response);
        // Backend возвращает { success: true, data: { applications: [...], pagination: {...} } }
        // А мы ожидаем { applications: [...], pagination: {...} }
        if (response.data) {
          return {
            applications: response.data.applications || [],
            pagination: response.data.pagination || { page: 1, limit: 10, total: 0, pages: 0 }
          };
        }
        return response;
      },
      providesTags: ['Application'],
    }),

    // Получение заявки по ID
    getApplicationById: builder.query<Application, number>({
      query: (id) => {
        console.log('🔍 getApplicationById query called with ID:', id);
        return `/applications/${id}`;
      },
      transformResponse: (response: any) => {
        console.log('🔄 Get Application By ID API Response transformation:', response);
        
        // 🔍 ДЕТАЛЬНАЯ ДИАГНОСТИКА: Логируем данные груза
        if (response.data && response.data.cargo && Array.isArray(response.data.cargo)) {
          console.log('🔍 🔍 🔍 ДАННЫЕ ГРУЗА ПОСЛЕ ТРАНСФОРМАЦИИ RTK Query:');
          response.data.cargo.forEach((cargo: any, index: number) => {
            console.log(`  Груз ${index} (${cargo.name}):`, {
              id: cargo.id,
              lengthM: { value: cargo.lengthM, type: typeof cargo.lengthM, isNull: cargo.lengthM === null },
              widthM: { value: cargo.widthM, type: typeof cargo.widthM, isNull: cargo.widthM === null },
              heightM: { value: cargo.heightM, type: typeof cargo.heightM, isNull: cargo.heightM === null },
              volumeM3: { value: cargo.volumeM3, type: typeof cargo.volumeM3, isNull: cargo.volumeM3 === null }
            });
          });
        }
        
        // Backend возвращает { success: true, data: application }
        // А мы ожидаем application
        return response.data || response;
      },
      providesTags: (result, error, id) => [{ type: 'Application', id }],
      onQueryStarted: async (id, { queryFulfilled }) => {
        console.log('🚀 getApplicationById query started for ID:', id);
        try {
          const result = await queryFulfilled;
          console.log('✅ getApplicationById query successful:', result);
        } catch (error) {
          console.error('❌ getApplicationById query failed:', error);
        }
      },
    }),

    // Получение единой истории событий по заявке
    getApplicationEvents: builder.query<ApplicationEvent[], number>({
      query: (applicationId) => `/applications/${applicationId}/events`,
      transformResponse: (response: any) => response.data || response || [],
      providesTags: (result, error, applicationId) => [
        { type: 'Application', id: applicationId },
        { type: 'Application', id: `events-${applicationId}` }
      ]
    }),

    // Создание заявки
    createApplication: builder.mutation<Application, CreateApplicationRequest>({
      query: (application) => ({
        url: '/applications',
        method: 'POST',
        body: application,
      }),
      transformResponse: (response: any) => {
        console.log('🔄 Create API Response transformation:', response);
        // Backend возвращает { success: true, data: application }
        return response.data || response;
      },
      invalidatesTags: ['Application', notificationListTag],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(notificationsApi.util.invalidateTags([notificationListTag]));
        } catch {
          /* mutation failed — do not refresh bell */
        }
      },
    }),

    // Обновление заявки
    updateApplication: builder.mutation<Application, UpdateApplicationRequest>({
      query: ({ id, ...application }) => ({
        url: `/applications/${id}`,
        method: 'PUT',
        body: application,
      }),
      transformResponse: (response: any) => {
        console.log('🔄 Update API Response transformation:', response);
        // Backend возвращает { success: true, data: application }
        return response.data || response;
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Application', id },
        'Application',
      ],
    }),

    // Обновление статуса заявки
    updateApplicationStatus: builder.mutation<Application, UpdateApplicationStatusRequest>({
      query: ({ id, ...data }) => ({
        url: `/applications/${id}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id, status }) => [
        { type: 'Application', id },
        'Application',
        ...(String(status).toUpperCase() === 'CANCELLED' ? (['Route'] as const) : []),
      ],
    }),

    // Отмена заявки (мягкая: status CANCELLED + каскад на маршруты/обязательства/счета)
    deleteApplication: builder.mutation<{ message: string; data?: Application }, number>({
      query: (id) => ({
        url: `/applications/${id}?confirm=true`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Application', 'Route'],
    }),

    // Обновление груза в заявке
    updateApplicationCargo: builder.mutation<any, {
      applicationId: number;
      cargoId: number;
      quantity?: number;
      weight?: number;
      volume?: number;
      description?: string;
    }>({
      query: ({ applicationId, cargoId, ...data }) => ({
        url: `/applications/${applicationId}/cargo/${cargoId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { applicationId }) => [
        { type: 'Application', id: applicationId },
        'Application',
      ],
    }),

    // Запуск счётчика оплаты (предоплата или доплата)
    startPaymentCounter: builder.mutation<any, { 
      id: number; 
      counterType: 'prepayment' | 'main';
      condition: string;
      startDate?: string;
      invoiceNumber?: string;
      otherCondition?: string;
    }>({
      query: ({ id, ...body }) => ({
        url: `/applications/${id}/payment/start-counter`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Application', id },
        'Application',
      ],
    }),

    // Отметить оплату как выполненную
    markPaymentPaid: builder.mutation<any, { id: number; paymentType: 'prepayment' | 'main' }>({
      query: ({ id, paymentType }) => ({
        url: `/applications/${id}/payment/mark-paid`,
        method: 'POST',
        body: { paymentType },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Application', id },
        'Application',
      ],
    }),

    // Обновить платежи от клиента (для своих заявок)
    updateClientPayments: builder.mutation<any, {
      id: number;
      prepaymentStatus?: string;
      prepaymentReceivedAmount?: number;
      prepaymentReceivedDate?: string;
      mainPaymentStatus?: string;
      mainPaymentReceivedAmount?: number;
      mainPaymentReceivedDate?: string;
    }>({
      query: ({ id, ...body }) => ({
        url: `/applications/${id}/client-payments`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Application', id },
        'Application',
      ],
    }),
  }),
});

export const {
  useGetApplicationsQuery,
  useLazyGetApplicationsQuery,
  useGetApplicationByIdQuery,
  useLazyGetApplicationByIdQuery,
  useGetApplicationEventsQuery,
  useCreateApplicationMutation,
  useUpdateApplicationMutation,
  useUpdateApplicationStatusMutation,
  useDeleteApplicationMutation,
  useUpdateApplicationCargoMutation,
  useGetClientsForFilterQuery,
  useStartPaymentCounterMutation,
  useMarkPaymentPaidMutation,
  useUpdateClientPaymentsMutation,
} = applicationsApi; 